import axios from 'axios';

import { DateFormat } from '../../@paco/types/dateFormatTypes';
import {
    ABSENCES,
    ABSENCES_OVERVIEW,
    AVAILABILITY_CALENDAR,
    LEAVE_OF_ABSENCE_REQUESTS,
    LEAVE_OF_ABSENCE_REQUESTS_CALENDAR,
    LIST_VIEW,
    PAGE_SIZES,
    PZ_COMPLETE_PERIOD,
    TRACKS_FINISHED,
    TYPE_ABSENCE,
    TYPE_LEAVE_OF_ABSENCE,
} from '../../constants';
import {
    addDays,
    formatDate,
    getEndOfWorkDay,
    getStartOfWorkDay,
} from '../date';
import { getLoketBridgeApiUrl } from '../index';

export const getLeaveOfAbsenceRequestIncludes = (page) => {
    switch (page) {
        case LEAVE_OF_ABSENCE_REQUESTS:
            return [
                'user',
                'user.experience',
                'user.roles',
                'overlappingLeaveOfAbsences',
                'comments',
                'comments.owner.person',
                'comments.owner.roles',
                'reviewedByUser',
                'reviewedByUser.roles',
                'leaveOfAbsenceHours',
                'leaveOfAbsenceHours.payrollPeriod',
            ];
        case PZ_COMPLETE_PERIOD:
            return [
                'user',
                'user.experience',
                'user.mainDepartment',
                'user.roles',
                'user.employmentType',
                'comments',
                'comments.owner.person',
                'comments.owner.roles',
                'reviewedByUser',
                'reviewedByUser.roles',
                'leaveOfAbsenceHours',
                'leaveOfAbsenceHours.payrollPeriod',
            ];
        case AVAILABILITY_CALENDAR:
        case LIST_VIEW:
            return [
                'user',
                'user.experience',
                'user.roles',
                'comments',
                'comments.owner.person',
                'comments.owner.roles',
                'reviewedByUser',
                'reviewedByUser.roles',
                'leaveOfAbsenceHours',
                'leaveOfAbsenceHours.payrollPeriod',
            ];
        case LEAVE_OF_ABSENCE_REQUESTS_CALENDAR:
            return [
                'user',
                'user.experience',
                'user.roles',
                'comments',
                'comments.owner.person',
                'comments.owner.roles',
                'reviewedByUser',
                'reviewedByUser.roles',
                'leaveOfAbsenceHours',
                'leaveOfAbsenceHours.payrollPeriod',
            ];
        case TRACKS_FINISHED:
            return ['user', 'leaveOfAbsenceHours'];
        default:
            return ['user'];
    }
};

const getLeaveOfAbsences = (
    startDate,
    endDate,
    status,
    departments = [],
    filter,
    page,
    pagination,
    includePendingChangesets = false,
) => {
    const includes = getLeaveOfAbsenceRequestIncludes(page);
    const size = PAGE_SIZES.ABSENCES[page];
    const {
        userTypes,
        employeeSearch,
        onlyShowMainDepartment,
        employeeContractTypes,
    } = filter || {};

    const givenStartDate = page !== LEAVE_OF_ABSENCE_REQUESTS ? startDate : addDays(new Date(), -1);
    const givenEndDate = page !== LEAVE_OF_ABSENCE_REQUESTS ? endDate : undefined;

    const formattedStartDate = formatDate(getStartOfWorkDay(givenStartDate), DateFormat.apiDateTime);
    const formattedEndDate = givenEndDate ? formatDate(getEndOfWorkDay(givenEndDate), DateFormat.apiDateTime) : undefined;

    const roles = (userTypes && userTypes.length > 0)
        ? JSON.stringify(userTypes)
        : undefined;

    const employmentTypes = (employeeContractTypes && employeeContractTypes.length > 0)
        ? JSON.stringify(employeeContractTypes)
        : undefined;

    return axios.get(
        `${process.env.REACT_APP_API_URL}/leave-of-absences/`,
        {
            params: {
                include: includes.join(','),
                ...(employeeSearch && { search: employeeSearch }),
                departments: onlyShowMainDepartment ? [] : departments,
                mainDepartments: onlyShowMainDepartment ? departments : [],
                onlyShowMatchingDepartments: departments.length > 0,
                startDate: formattedStartDate,
                endDate: formattedEndDate,
                statuses: JSON.stringify(status),
                roles,
                employmentTypes,
                ...(pagination && { 'page[number]': pagination.number || 1 }),
                ...(size && { 'page[size]': size }),
                ...(includePendingChangesets && { includePendingChangesets: 'true' }),
            },
        },
    );
};

const getAbsences = (filter, departments, pagination) => {
    const { employeeSearch, userTypes, employeeContractTypes } = filter;

    const includes = [
        'user',
        'user.experience',
        'user.roles',
        'comments',
        'overlappingShifts',
        'overlappingShifts.department',
        'overlappingShifts.plannedUsers',
        'overlappingShifts.plannedUsers.roles',
        'overlappingShifts.plannedUsers.employmentType',
        'absenceHours',
        'absenceHours.payrollPeriod',
    ];

    const size = PAGE_SIZES.ABSENCES[ABSENCES];

    const roles = (userTypes && userTypes.length > 0)
            ? JSON.stringify(userTypes)
            : undefined;

    const employmentTypes = (employeeContractTypes && employeeContractTypes.length > 0)
        ? JSON.stringify(employeeContractTypes)
        : undefined;

    return axios.get(
        `${process.env.REACT_APP_API_URL}/absences/`,
        {
            params: {
                include: includes.join(','),
                search: employeeSearch,
                departments,
                roles,
                showOpenAbsencesOnly: true,
                employmentTypes,
                'page[number]': pagination.number || 1,
                'page[size]': size,
            },
        },
    );
};

const getAbsencesInDateRange = (
    startDate,
    endDate,
    filter,
    departments,
    pagination,
    includes = ['user', 'user.roles', 'absenceHours', 'absenceHours.payrollPeriod'],
) => {
    const size = pagination && PAGE_SIZES.ABSENCES[ABSENCES_OVERVIEW];
    const { userTypes, employeeContractTypes } = filter || {};

    const formattedStartDate = formatDate(getStartOfWorkDay(startDate), DateFormat.apiDateTime);
    const formattedEndDate = formatDate(getEndOfWorkDay(endDate), DateFormat.apiDateTime);

    const roles = (userTypes && userTypes.length > 0)
        ? JSON.stringify(userTypes)
        : undefined;

    const employmentTypes = (employeeContractTypes && employeeContractTypes.length > 0)
        ? JSON.stringify(employeeContractTypes)
        : undefined;

    return axios.get(
        `${process.env.REACT_APP_API_URL}/absences/`,
        {
            params: {
                include: includes.join(','),
                ...(filter && { search: filter.employeeSearch }),
                ...(departments && { departments }),
                ...(pagination && { 'page[number]': pagination.number || 1 }),
                ...(size && { 'page[size]': size }),
                startDate: formattedStartDate,
                endDate: formattedEndDate,
                roles,
                employmentTypes,
            },
        },
    );
};

const getAbsencesInDateRangeCSV = (
    startDate,
    endDate,
    departmentIds,
    filter,
) => {
    const { userTypes } = filter;

    const formattedStartDate = formatDate(getStartOfWorkDay(startDate), DateFormat.apiDateTime);
    const formattedEndDate = formatDate(getEndOfWorkDay(endDate), DateFormat.apiDateTime);

    return axios.get(
        `${process.env.REACT_APP_API_URL}/absences/csv`,
        {
            params: {
                search: filter.employeeSearch,
                departments: departmentIds,
                startDate: formattedStartDate,
                endDate: formattedEndDate,
                roles: (userTypes && userTypes.length > 0) ? JSON.stringify(userTypes) : undefined,
            },
        },
    );
};

const patchAbsence = (
    id,
    startDate = null,
    endDate = null,
) => {
    const body = {
        id,
        type: 'absences',
        attributes: {
            start: startDate,
            ...(endDate && { end: endDate }),
        },
    };
    return axios.patch(
        `${process.env.REACT_APP_API_URL}/absences/${id}/`,
        { data: body },
    );
};

const postLeaveOfAbsencesToLoket = (startDate, endDate) => {
    const lastParam = endDate ? `&to=${endDate}` : '';

    return axios.put(
        `${getLoketBridgeApiUrl()}/leave/sync?from=${startDate}${lastParam}/`,
    );
};

export const generateAbsenceHoursBody = (
    absenceId,
    payrollPeriodId,
    hours,
    weekNumber,
    type = 'absenceHours',
    leaveType,
) => ({
    type,
    attributes: {
        ...(leaveType && { leaveType }),
        hours,
        ...(weekNumber && { weekNumber }),
    },
    relationships: {
        [type === 'absenceHours' ? 'absence' : 'leaveOfAbsence']: {
            data: {
                type: type === 'absenceHours' ? TYPE_ABSENCE : TYPE_LEAVE_OF_ABSENCE,
                id: absenceId,
            },
        },
        ...(payrollPeriodId && {
            payrollPeriod: {
                data: {
                    type: 'payrollPeriods',
                    id: payrollPeriodId,
                },
            },
        }),
    },
});

const deleteAbsenceHours = (id) => axios.delete(`${process.env.REACT_APP_API_URL}/absence-hours/${id}`);

export {
    getLeaveOfAbsences,
    getAbsences,
    getAbsencesInDateRange,
    patchAbsence,
    getAbsencesInDateRangeCSV,
    deleteAbsenceHours,
    postLeaveOfAbsencesToLoket,
};
